import { useState, useEffect } from "react";
import { FileText } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ProviderSummary = ({ npi, name, specialty, city, state, entityType, enumerationDate }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let retryCount = 0;

    const fetchSummary = async () => {
      try {
        const params = new URLSearchParams({
          name: name || "",
          specialty: specialty || "",
          city: city || "",
          state: state || "",
          entity_type: entityType || "",
          enumeration_date: enumerationDate || "",
        });
        const res = await fetch(`${API_URL}/api/enrichment/${npi}/summary?${params}`);
        if (!res.ok) throw new Error("fail");
        const data = await res.json();

        if (cancelled) return;

        if (data.has_summary && data.sentence_count > 1) {
          setSummary(data);
          setLoading(false);
        } else if (retryCount < 2) {
          // Data might still be enriching, retry once
          retryCount++;
          setTimeout(fetchSummary, 5000);
        } else {
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    };

    fetchSummary();
    return () => { cancelled = true; };
  }, [npi, name, specialty, city, state, entityType, enumerationDate]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-5 mb-6 animate-pulse" data-testid="summary-skeleton">
        <div className="h-3 bg-slate-200 rounded w-3/4 mb-3" />
        <div className="h-3 bg-slate-200 rounded w-full mb-2" />
        <div className="h-3 bg-slate-200 rounded w-5/6 mb-2" />
        <div className="h-3 bg-slate-200 rounded w-2/3" />
      </div>
    );
  }

  if (!summary) return null;

  return (
    <div
      className="bg-gradient-to-br from-white to-slate-50 rounded-lg border border-slate-200 p-5 mb-6"
      data-testid="provider-summary"
    >
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-teal" weight="bold" />
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Provider Overview</span>
        {summary.data_sources?.length > 0 && (
          <span className="ml-auto text-[10px] text-slate-400">
            {summary.data_sources.length} data sources
          </span>
        )}
      </div>
      <p className="text-sm text-slate-700 leading-relaxed" data-testid="summary-text">
        {summary.summary}
      </p>
    </div>
  );
};
